import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { collection, getDocs, deleteDoc, doc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiTrash2, FiEye, FiCalendar } from "react-icons/fi";

const EventRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "eventRegistrations"), orderBy("createdAt", "desc")));
      setRegistrations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    await deleteDoc(doc(db, "eventRegistrations", id));
    fetch();
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Event Registrations</h1>
            <p className="text-sm text-gray-400">{registrations.length} registration{registrations.length !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={fetch} className="px-4 py-2 border border-gray-300 rounded-lg text-[12px] font-semibold hover:border-[#e84fa5] transition">Refresh</button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div>
        ) : registrations.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400 text-[13px]">No registrations yet.</div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">#</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Event</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Phone</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-[11px] font-extrabold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((r, i) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3 text-[12px] text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 text-[12px] font-semibold text-black max-w-[180px] truncate">{r.eventTitle || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-pink-100 flex items-center justify-center text-[11px] font-bold text-[#e84fa5] shrink-0">
                          {r.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <span className="text-[12px] font-semibold text-black">{r.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-gray-600">{r.email}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-600">{r.phone || "—"}</td>
                    <td className="px-4 py-3 text-[11px] text-gray-400 whitespace-nowrap">{r.createdAt?.toDate?.().toLocaleDateString() || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setViewing(r)}
                          className="p-1.5 text-gray-400 hover:text-[#e84fa5] hover:bg-pink-50 rounded-lg transition" title="View">
                          <FiEye size={14} />
                        </button>
                        <button onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete">
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View Modal */}
        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setViewing(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center text-[16px] font-bold text-[#e84fa5]">
                    {viewing.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <h3 className="text-[15px] font-extrabold text-black">{viewing.name}</h3>
                    <p className="text-[11px] text-gray-400">Event Registration</p>
                  </div>
                </div>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700 text-[20px] leading-none">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Event</p>
                    <p className="text-[13px] font-semibold text-black">{viewing.eventTitle || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Event ID</p>
                    <p className="text-[12px] text-gray-500 truncate">{viewing.eventId || "—"}</p>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Email</p>
                  <p className="text-[13px] text-black">{viewing.email}</p>
                </div>
                {viewing.phone && (
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Phone</p>
                    <p className="text-[13px] text-black">{viewing.phone}</p>
                  </div>
                )}
                {viewing.message && (
                  <div>
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Message</p>
                    <p className="text-[13px] text-gray-700 leading-relaxed bg-gray-50 rounded-lg px-3 py-3 whitespace-pre-wrap">{viewing.message}</p>
                  </div>
                )}
                {viewing.createdAt?.toDate && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wide mb-1">Registered On</p>
                    <p className="text-[12px] text-gray-500">{viewing.createdAt.toDate().toLocaleString()}</p>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button onClick={() => setViewing(null)} className="px-6 py-2 bg-black text-white text-[11px] font-bold uppercase rounded-lg hover:bg-gray-800 transition">Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default EventRegistrations;
